import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Activity } from "lucide-react";
import { OperationalCard, FinancialCard, BalanceSheetCard, QualityCard, StaffInfraCard } from "@/components/round-detail/DetailCards";

function ScoreBar({ label, value, color }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-600">{label}</span>
        <span className="font-bold tabular-nums">{value.toFixed(1)}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
    </div>
  );
}

export default function RoundDetailPanel({ hospitalId, currentRound }) {
  const { t } = useTranslation();
  const [selectedRound, setSelectedRound] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentRound > 1) setSelectedRound(String(currentRound - 1));
    else setSelectedRound(null);
  }, [currentRound]);

  useEffect(() => {
    if (!selectedRound || !hospitalId) return;
    setLoading(true);
    axios.get(`${API}/hospitals/${hospitalId}/round-detail/${parseInt(selectedRound)}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [selectedRound, hospitalId]);

  const rounds = Array.from({ length: Math.max(0, currentRound - 1) }, (_, i) => i + 1);

  if (rounds.length === 0) {
    return (
      <Card><CardContent className="p-6 text-center text-gray-400">
        <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">{t("hospital.noYearResults") || "No hay resultados todavía. Espera a que avance el primer año."}</p>
      </CardContent></Card>
    );
  }

  const f = detail?.financials || {};
  const bs = detail?.balance_sheet || {};
  const scores = f.scores || {};

  return (
    <div className="space-y-4" data-testid="round-detail-panel">
      <div className="flex items-center gap-3">
        <h3 className="font-heading font-bold text-lg">{t("hospital.yearResults")}</h3>
        <Select value={selectedRound || ""} onValueChange={setSelectedRound}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t("hospital.selectYear") || "Seleccionar año"} /></SelectTrigger>
          <SelectContent>
            {rounds.map(r => <SelectItem key={r} value={String(r)}>{t("common.round")} {r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-gray-200 border-t-emerald-600 rounded-full animate-spin" /></div>
      ) : !detail ? (
        <Card><CardContent className="p-6 text-center text-gray-400">{t("hospital.selectYearHint") || "Selecciona un año para ver resultados"}</CardContent></Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" />{t("hospital.overallScore") || "Puntuación Global"}
                <Badge className="ml-auto text-lg px-3 py-1" variant={scores.overall >= 60 ? "default" : "destructive"}>
                  {scores.overall?.toFixed(1) || "-"} / 100
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <ScoreBar label={t("hospital.financialPerformance") || "Rendimiento Financiero"} value={scores.financial || 0} color="bg-emerald-500" />
              <ScoreBar label={t("hospital.operationalEfficiency") || "Eficiencia Operativa"} value={scores.operational || 0} color="bg-blue-500" />
              <ScoreBar label={t("hospital.quality")} value={scores.quality || 0} color="bg-pink-500" />
              <ScoreBar label={t("hospital.staffSat")} value={scores.staff || 0} color="bg-indigo-500" />
              <ScoreBar label={t("hospital.reputation")} value={scores.reputation || 0} color="bg-amber-500" />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OperationalCard ops={f.operations || {}} />
            <FinancialCard revenue={f.revenue || {}} expenses={f.expenses || {}} netProfit={f.net_profit || 0} />
            <BalanceSheetCard bs={bs} />
            <QualityCard ratings={f.ratings || {}} decisions={detail?.decision_snapshot || {}} />
            <StaffInfraCard staff={f.staff || {}} infra={f.infrastructure || {}} ratings={f.ratings || {}} />
          </div>
        </>
      )}
    </div>
  );
}
